// /src/apis/users.ts

import { AxiosError } from 'axios';

import API from './API';
import { PageParams, PageResponse } from '@models/api';
import { User, UserGroup, UserResponse, UserStatus } from '@models/user';

export type SignUpRequest = {
  username: string;
  password: string;
  name: string;
  position: string;
  group: UserGroup;
  email: string;
  phoneNumber: string;
  status: UserStatus;
};

export type EditUserRequest = {
  userId: number;
  position: string;
  group: UserGroup;
  email: string;
  phoneNumber: string;
  status: UserStatus;
};

export type GetUsersParams = {
  username?: string;
  name?: string;
  group?: UserGroup;
  status?: UserStatus;
};

export type ChangePasswordRequest = {
  username: string;
  oldPassword: string;
  newPassword: string;
};

export type RecoverPasswordRequest = {
  username: string;
  email: string;
};

/**
 * Fetch the current authenticated user.
 *
 * NOTE:
 * The backend sometimes returns 2xx with an empty `data` array when the session is invalid.
 * In that case, we fail fast with a user-friendly error.
 */
export const getMe = async (): Promise<UserResponse> => {
  const res = await API.get<PageResponse<UserResponse>>('/v1/users/me');
  const me = res.data?.data?.[0];

  if (!me) {
    throw new Error('Session is invalid. Please sign in again.');
  }

  return me;
};

export const getUsersCount = async (params: GetUsersParams): Promise<number> => {
  const res = await API.get<PageResponse<number>>('/v1/users/count', { params });
  return res.data.data?.[0] ?? 0;
};

export const getUsers = async (
  params: PageParams & GetUsersParams
): Promise<PageResponse<User>> => (await API.get<PageResponse<User>>('/v1/users', { params })).data;

export const getUser = async (id: number): Promise<PageResponse<User>> =>
  (await API.get<PageResponse<User>>(`/v1/users/${id}`)).data;

export const getUserByUsername = async (username: string): Promise<PageResponse<User>> =>
  (await API.get<PageResponse<User>>(`/v1/users/username/${username}`)).data;

export const getUserByEmail = async (email: string): Promise<PageResponse<User>> =>
  (
    await API.get<PageResponse<User>>('/v1/users/check-email', {
      params: { email },
    })
  ).data;

export const signUp = async (data: SignUpRequest): Promise<PageResponse<User>> =>
  (await API.post<PageResponse<User>>('/v1/users', data)).data;

export const createUser = async (data: SignUpRequest): Promise<PageResponse<User>> =>
  (await API.post<PageResponse<User>>('/v1/users', data)).data;

export const updateUser = async (
  id: number,
  request: EditUserRequest
): Promise<PageResponse<User>> => (await API.put<PageResponse<User>>(`/v1/users/${id}`, request)).data;

export const deleteUser = async (userId: number): Promise<void> => {
  await API.delete(`/v1/users/${userId}`);
};

export const changePassword = async (
  userId: number,
  data: ChangePasswordRequest
): Promise<void> => {
  await API.post(`/v1/users/${userId}/change-password`, data);
};

/**
 * Recover password (send a temporary password via email).
 *
 * We intentionally normalize common backend errors (400/401/404) into a single,
 * user-friendly message to avoid leaking sensitive information.
 */
export const recoverPassword = async (
  recoverPasswordData: RecoverPasswordRequest
): Promise<PageResponse<unknown>> => {
  try {
    const res = await API.post<PageResponse<unknown>>(
      '/v1/users/recover-password',
      recoverPasswordData
    );
    return res.data;
  } catch (err) {
    const e = err as AxiosError<any>;
    const status = e.response?.status;

    // Even if the backend returns different status codes (400/401/404),
    // from the user's perspective the UX should be the same.
    if (status === 400 || status === 401 || status === 404) {
      throw new Error('No matching account found for the username and email.');
    }

    throw new Error('Failed to recover password. Please try again.');
  }
};
